import { getSiteTemplates } from '@lms/api-client/backend';
import { Button } from '@lms/ui/button';
import { Card, CardContent } from '@lms/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@lms/ui/table';
import { LoaderCircle, PlusIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { useTranslations } from 'use-intl';
import {
  PageBox,
  PageButtons,
  PageHeader,
  PageTitle,
} from '@/components/admin/page-box';
import { DeleteConfirmationMenu } from '@/components/delete-confirmation-menu';
import { useBrand } from '@/features/branding/brand-provider';
import { removeSiteTemplate } from './site-templates-api';

// Ports app/[brand]/admin/templates/page.tsx together with its
// _components/templates-list.tsx (which rendered the shared TableListView).
// Data comes from SWR instead of the server component's await; delete
// revalidates the same key (the Next action used revalidatePath).
export function TemplatesList() {
  const t = useTranslations('admin');
  const { slug } = useBrand();

  const {
    data: templates,
    isLoading,
    mutate,
  } = useSWR('admin/site-templates', () => getSiteTemplates());

  return (
    <PageBox>
      <PageHeader>
        <PageTitle>{t('templates')}</PageTitle>
        <PageButtons>
          <Button asChild>
            <Link to={`/${slug}/admin/templates/add`}>
              <PlusIcon className="size-4" />
              {t('add', { what: t('template').toLowerCase() })}
            </Link>
          </Button>
        </PageButtons>
      </PageHeader>
      <Card>
        <CardContent className="mt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('site')}</TableHead>
                <TableHead>{t('name')}</TableHead>
                <TableHead>{t('description')}</TableHead>
                <TableHead className="w-5" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates?.map((tpl) => (
                <TableRow key={tpl.id}>
                  <TableCell>{tpl.siteName}</TableCell>
                  <TableCell>{tpl.name}</TableCell>
                  <TableCell>{tpl.description}</TableCell>
                  <TableCell>
                    <DeleteConfirmationMenu
                      id={tpl.id}
                      onDelete={removeSiteTemplate}
                      onDeleted={() => mutate()}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <LoaderCircle className="text-primary size-6 animate-spin" />
            </div>
          )}
          {!isLoading && (templates?.length ?? 0) === 0 && (
            <p className="text-muted-foreground py-8 text-center text-sm">
              {t('list', { what: t('templates').toLowerCase() })}
            </p>
          )}
        </CardContent>
      </Card>
    </PageBox>
  );
}
